import React from 'react'

// definitions
import * as AppDefinitions from '../../../App.d'

// components
import { FormGroup, Button, Form, OverlayTrigger, Tooltip, Modal, ButtonGroup, Dropdown, DropdownButton, Row, Col } from 'react-bootstrap'
import { Calendar as CalendarData } from '../../../../../back-end/utilities/apiDefinitions'

// utillites
import * as Request from '../../../utilities/request'
import { HelpCircle, Trash2 } from 'react-feather'
import { MessageAction } from '../../ui/messages/message'
import { FormControlLabel, Checkbox } from '@material-ui/core'
import { ColorPicker } from 'material-ui-color'
import { allColours } from './leaveTypePopup'
import { CalendarInputFeed } from '../../../../../back-end/common/calendarInputFeed'

interface CustomFeedPopUpProps {
	show: boolean
	appState: AppDefinitions.AppState
	updateMessages: (message: MessageAction) => void
	currentCalendar: CalendarData
	updateCalendar: (cal: CalendarData) => void
	handleClose: () => void
	titleText: string
	type: 'Add' | 'Edit'
	currentFeed?: CalendarInputFeed
	handleCustomFeedUpdate: (inputFeed: CalendarInputFeed) => void
}

interface IcsFeed {
	calendarInputFeedID?: string
	name: string
	url: string
	isIncludedInConsole?: boolean
	hexColour?: string
}

export const presetIcsFeeds: IcsFeed[] = [
	{ name: 'Australia', url: 'https://www.officeholidays.com/ics/australia' },
	{ name: '\u00A0\u00A0NSW', url: 'https://www.officeholidays.com/ics/australia/new-south-wales' },
	{ name: '\u00A0\u00A0VIC', url: 'https://www.officeholidays.com/ics/australia/victoria' },
	{ name: '\u00A0\u00A0QLD', url: 'https://www.officeholidays.com/ics/australia/queensland' },
	{ name: '\u00A0\u00A0ACT', url: 'https://www.officeholidays.com/ics/australia/act' },
	{ name: '\u00A0\u00A0WA', url: 'https://www.officeholidays.com/ics/australia/western-australia' },
	{ name: '\u00A0\u00A0SA', url: 'https://www.officeholidays.com/ics/australia/south-australia' },
	{ name: '\u00A0\u00A0TAS', url: 'https://www.officeholidays.com/ics/australia/tasmania' },
	{ name: '\u00A0\u00A0NT', url: 'https://www.officeholidays.com/ics/australia/northern-territory' },
	{ name: 'United Kingdom', url: 'https://www.officeholidays.com/ics/united-kingdom' },
	{ name: 'New Zealand', url: 'https://www.officeholidays.com/ics/new-zealand' },
	{ name: 'Philippines', url: 'https://www.officeholidays.com/ics/philippines' },
]

const initialIcsFeed = {
	name: '',
	url: '',
	isIncludedInConsole: false,
	hexColour: '#86a581',
}

export const CustomFeedPopUp = (props: CustomFeedPopUpProps) => {
	const [inputFeed, updateInputFeed] = React.useState<CalendarInputFeed | IcsFeed>(
		props.currentFeed && props.type === 'Edit' ? props.currentFeed : initialIcsFeed
	)

	const handleCustomFeedCreation = async () => {
		try {
			const feed = await Request.post(`inputfeed/check`, inputFeed, props.appState.authState)
			if (feed.data && feed.data.isValid) {
				const req = await Request.post(`calendar/${props.currentCalendar.calendarID}/inputfeed`, inputFeed, props.appState.authState)

				if (req.data.success) {
					props.updateMessages({
						type: 'add',
						data: {
							severity: 'success',
							message: 'Successfully added external calendar',
							timeout: 3000,
							dismissible: true,
						},
					})
				}
				props.updateCalendar({
					...props.currentCalendar,
					inputFeeds: req.data.calendarInputFeeds,
				})
			} else {
				props.updateMessages({
					type: 'add',
					data: {
						severity: 'danger',
						message: 'That does not look like an ics feed',
						timeout: 3000,
						dismissible: true,
					},
				})
			}
		} catch (err) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: 'Unable to add external calendar',
					timeout: 3000,
					dismissible: true,
				},
			})
		}
	}

	// delete calendar input feed
	const deleteCalendarFeed = async () => {
		try {
			const req = await Request.del(`calendar/${props.currentCalendar.calendarID}/inputfeed/${inputFeed.calendarInputFeedID!}`, props.appState.authState)

			if (req.data.success && props.currentCalendar && props.currentCalendar.inputFeeds) {
				props.updateCalendar({
					...props.currentCalendar,
					inputFeeds: props.currentCalendar.inputFeeds.filter((feed) => feed.calendarInputFeedID !== inputFeed.calendarInputFeedID!),
				})
				props.updateMessages({
					type: 'add',
					data: {
						severity: 'success',
						message: 'Successfully deleted external calendar',
						timeout: 3000,
						dismissible: true,
					},
				})
			}
		} catch (err) {
			props.updateMessages({
				type: 'add',
				data: {
					severity: 'danger',
					message: 'Unable to delete external calendar',
					timeout: 3000,
					dismissible: true,
				},
			})
		}
	}

	return (
		<Modal show={props.show} aria-labelledby="modal" centered onHide={props.handleClose}>
			{props.titleText ? (
				<Modal.Header>
					<Modal.Title>{props.titleText}</Modal.Title>
					<OverlayTrigger
						placement="auto"
						overlay={
							<Tooltip id={`tooltip-xero`}>
								An external calendar (.ics) feed can combine an external calendar with our graphical and ics feeds.
							</Tooltip>
						}
					>
						<div>
							<HelpCircle className={'settings-plus'} />
						</div>
					</OverlayTrigger>
				</Modal.Header>
			) : null}
			<Modal.Body>
				<FormGroup>
					<DropdownButton
						as={ButtonGroup}
						id={'dropdown'}
						variant={'primary'}
						title={inputFeed ? presetIcsFeeds.find((e) => e.url === inputFeed.url)?.name ?? 'Custom URL' : 'Select one'}
						onSelect={(e) => {
							if (e) {
								updateInputFeed({ ...inputFeed, url: e })
							}
						}}
						alignRight
						style={{
							paddingLeft: '0px',
							marginBottom: '15px',
							fontSize: '14px',
						}}
						disabled={props.type === 'Edit'}
					>
						<Dropdown.Header>Public Holidays</Dropdown.Header>
						{presetIcsFeeds.map((e) => (
							<Dropdown.Item key={e.name} eventKey={e.url} active={inputFeed.url === e.url}>
								{e.name}
							</Dropdown.Item>
						))}
					</DropdownButton>
					<Row style={{ paddingLeft: '16px', alignItems: 'center' }}>
						<div className="settings-colourpicker">
							<ColorPicker
								onChange={(value) => updateInputFeed({ ...inputFeed, hexColour: '#' + value.hex })}
								value={inputFeed.hexColour}
								palette={allColours}
								hideTextfield
								disableAlpha
								disableTextfield
								inputFormats={[]}
							/>
						</div>
						<OverlayTrigger
							key={inputFeed.calendarInputFeedID}
							placement="right"
							overlay={<Tooltip id={`tooltip-ical Feed`}>{inputFeed.url}</Tooltip>}
						>
							<Form.Control
								type="text"
								value={inputFeed.url}
								style={{ width: '75%' }}
								disabled={props.type === 'Edit'}
								placeholder="https://example.com/calendar-name.ics"
								onChange={(e) => {
									const value = e.target.value
									updateInputFeed({ ...inputFeed, url: value })
								}}
							/>
						</OverlayTrigger>
						{props.type === 'Edit' ? (
							<Col sm="2" style={{ flex: '1', textAlign: 'right' }}>
								<OverlayTrigger placement="right" overlay={<Tooltip id={`tooltip-remove-input-feed`}>Delete Feed</Tooltip>}>
									<Trash2
										style={{
											cursor: 'pointer',
											width: '30px',
											height: '30px',
											textAlign: 'right',
										}}
										onClick={() => {
											props.handleClose()
											deleteCalendarFeed()
										}}
									/>
								</OverlayTrigger>
							</Col>
						) : null}
					</Row>
					<Row style={{ paddingLeft: '5px' }}>
						<FormControlLabel
							control={
								<Checkbox
									color={'default'}
									checked={inputFeed.isIncludedInConsole}
									onChange={() => updateInputFeed({ ...inputFeed, isIncludedInConsole: !inputFeed.isIncludedInConsole })}
									name={'includeInConsole'}
								/>
							}
							label={'Include in console'}
						/>
						<OverlayTrigger
							placement="auto"
							overlay={
								<Tooltip id={`tooltip-ics`}>
									Selecting this option will allow users of LeaveCal console to see these events and they will also be included in any alerts.
								</Tooltip>
							}
						>
							<div style={{ padding: '7px' }}>
								<HelpCircle className={'settings-plus'} />
							</div>
						</OverlayTrigger>
					</Row>
					<Button
						variant={'success'}
						style={{ marginRight: '15px' }}
						onClick={() => {
							props.handleClose()
							props.type === 'Add' ? handleCustomFeedCreation() : props.handleCustomFeedUpdate(inputFeed)
						}}
					>
						Save
					</Button>
					<Button variant={'secondary'} onClick={props.handleClose}>
						Cancel
					</Button>
				</FormGroup>
			</Modal.Body>
		</Modal>
	)
}
